import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import MediaQuery from 'react-responsive';
import { map, toPlainObject } from 'lodash';

import {
    SPONSORSHIP_BONUS_AMOUNT,
    SPONSORSHIP_PURCHASE_THRESHOLD,
    SPONSORSHIP_BONUS_PERCENT
} from '../../../../constants/sponsorship';
import { PATH } from '../../../../constants/path';
import { STATIC_ROOT } from '../../../../constants';
import { MOMENTJS_DATE_FORMAT_STANDARD } from '../../../../constants/moment';

import {
    sendInvitationReminds,
    sendPurchaseReminds
} from '../../../../services/sponsorship/actions';
import { fetchAccountGodchildrenIfNeeded } from '../../../../services/account/godchildren/actions';

import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';
import { toDate, nowTimestamp, timestampPlusDays } from '../../../../utils/dateUtils';

import HeadComponent from '../../../HeadComponent';
import LocalizedBreadcrumbItem from '../../../breadcrumb/LocalizedBreadcrumbsItem';

import SvgComponent from '../../../SvgComponent';
import ButtonComponent from '../../../button/ButtonComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './GodchildrenPage.scss';

const REMIND_DAY_LIMIT = 2;

export class GodchildrenPage extends React.Component {
    componentDidMount() {
        const { fetchAccountGodchildrenIfNeeded } = this.props;
        fetchAccountGodchildrenIfNeeded();
    }

    showGodsonRemindButton = godson => {
        const { i18n, sendingPurchaseReminds } = this.props;

        if (sendingPurchaseReminds) {
            return <LoadingComponent />;
        } else if (godson && godson.gain === 0) {
            const alreadyReminded = godson.lastReminderDate
                ? timestampPlusDays(godson.lastReminderDate, REMIND_DAY_LIMIT) > nowTimestamp()
                : false;

            return (
                <ButtonComponent
                    type="button"
                    color="main"
                    text={
                        alreadyReminded ? i18n.ALREADY_REMINDED : i18n.GODCHILDREN_REMINDER_BUTTON
                    }
                    onClick={this.onSubmitPurchaseReminds(godson.id)}
                    disabled={alreadyReminded}
                />
            );
        }
        return null;
    };

    showRequestRemindButton = request => {
        const { i18n, sendingInvitationReminds } = this.props;

        if (sendingInvitationReminds) {
            return <LoadingComponent />;
        } else if (request && request.state === 'WAITING') {
            const alreadyReminded = request.lastReminder
                ? timestampPlusDays(request.lastReminder, REMIND_DAY_LIMIT) > nowTimestamp()
                : false;

            return (
                <ButtonComponent
                    type="button"
                    color="main"
                    text={
                        alreadyReminded ? i18n.ALREADY_REMINDED : i18n.INVITATIONS_REMINDER_BUTTON
                    }
                    onClick={this.onSubmitInvitReminds(request.id)}
                    disabled={alreadyReminded}
                />
            );
        }
        return null;
    };

    toIcon = state => {
        if (state === 'WAITING') {
            return (
                <SvgComponent
                    src={`${STATIC_ROOT}/icon/picto-attente.svg`}
                    width={17}
                    height={19}
                />
            );
        } else if (state === 'VALIDATED') {
            return (
                <SvgComponent src={`${STATIC_ROOT}/icon/picto-valide.svg`} width={17} height={13} />
            );
        } else if (state === 'VALIDATED_FIRST_PURCHASE_DONE') {
            return (
                <SvgComponent src={`${STATIC_ROOT}/icon/picto-paye.svg`} width={17} height={13} />
            );
        }
        return null;
    };

    onSubmitInvitReminds = id => event => {
        event.preventDefault();

        const { user, sendInvitationReminds } = this.props;
        const sponsorshipList = [id];

        sendInvitationReminds({ sponsorshipList, username: user.username });
    };

    onSubmitPurchaseReminds = id => event => {
        event.preventDefault();

        const { user, sendPurchaseReminds } = this.props;
        const godsonList = [id];

        sendPurchaseReminds({ godsonList, username: user.username });
    };

    render() {
        const { i18n, user, fetching, godchildren, requests } = this.props;

        const registered = godchildren.length;
        const buyers = godchildren.filter(godson => godson.gain > 0).length;
        const gains = godchildren.reduce((prev, curr) => prev + curr.gain, 0);

        return (
            <main id="p-compte-godchildren">
                <HeadComponent title={i18n.TITLE} description={i18n.DESCRIPTION} />
                <LocalizedBreadcrumbItem to={PATH.ACCOUNT_GODCHILDREN_PATH}>
                    {i18n.BREADCRUMB}
                </LocalizedBreadcrumbItem>

                <div className="sponsorship">
                    <h2>{i18n.SPONSORSHIP_TITLE}</h2>
                    <p>
                        {convertHtmlToReactDomWithParams(
                            i18n.SPONSORSHIP_TEXT,
                            SPONSORSHIP_BONUS_AMOUNT,
                            SPONSORSHIP_PURCHASE_THRESHOLD,
                            SPONSORSHIP_BONUS_PERCENT
                        )}
                    </p>
                </div>
                <h2>{i18n.GODCHILDREN_TITLE}</h2>
                <div className="summary">
                    <div className="referals">
                        <p>
                            {i18n.GODCHILDREN_SUMMARY_REGISTERED} <span>{registered}</span>
                        </p>
                        <p>
                            {i18n.GODCHILDREN_SUMMARY_BUYERS} <span>{buyers}</span>
                        </p>
                    </div>
                    <div className="reward">
                        <div className="date">
                            {convertHtmlToReactDomWithParams(
                                i18n.GODCHILDREN_SUMMARY_DATE,
                                toDate(user.creationDate, MOMENTJS_DATE_FORMAT_STANDARD)
                            )}
                        </div>
                        <div className="gain">
                            <span>
                                {convertHtmlToReactDomWithParams(
                                    i18n.GODCHILDREN_SUMMARY_REWARD,
                                    gains
                                )}
                            </span>
                        </div>
                    </div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>{i18n.GODCHILDREN_TH_EMAIL}</th>
                            <th className="hide-mobile">{i18n.GODCHILDREN_TH_DATE}</th>
                            <th className="gain">{i18n.GODCHILDREN_TH_GAIN}</th>
                            <th className="remind">{i18n.GODCHILDREN_TH_REMIND}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fetching ? (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td colSpan={`${matches ? '4' : '3'}`}>
                                            <LoadingComponent />
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        ) : godchildren.length > 0 ? (
                            map(
                                godchildren.sort((a, b) => a.id < b.id),
                                godson => (
                                    <tr key={godson.id}>
                                        <td className="email">{godson.email}</td>
                                        <td className="hide-mobile">
                                            {godson.creationDate
                                                ? toDate(
                                                      godson.creationDate,
                                                      MOMENTJS_DATE_FORMAT_STANDARD
                                                  )
                                                : null}
                                        </td>
                                        <td>{godson.gain}€</td>
                                        <td>{this.showGodsonRemindButton(godson)}</td>
                                    </tr>
                                )
                            )
                        ) : (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td className="no-data" colSpan={`${matches ? '4' : '3'}`}>
                                            {i18n.GODCHILDREN_NO_GODSON}
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h2>{i18n.INVITATIONS_TITLE}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>{i18n.INVITATIONS_TH_EMAIL}</th>
                            <th className="hide-mobile">{i18n.INVITATIONS_TH_DATE}</th>
                            <th className="state">{i18n.INVITATIONS_TH_STATE}</th>
                            <th className="remind">{i18n.INVITATIONS_TH_REMIND}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fetching ? (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td colSpan={`${matches ? '4' : '3'}`}>
                                            <LoadingComponent />
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        ) : requests.length > 0 ? (
                            map(
                                requests.sort((a, b) => a.id < b.id),
                                request => (
                                    <tr key={request.id}>
                                        <td className="email">{request.email}</td>
                                        <td className="hide-mobile">
                                            {request.date
                                                ? toDate(
                                                      request.date,
                                                      MOMENTJS_DATE_FORMAT_STANDARD
                                                  )
                                                : null}
                                        </td>
                                        <td>{this.toIcon(request.state)}</td>
                                        <td>{this.showRequestRemindButton(request)}</td>
                                    </tr>
                                )
                            )
                        ) : (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td className="no-data" colSpan={`${matches ? '4' : '3'}`}>
                                            {i18n.INVITATIONS_NO_INVITATION}
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        )}
                    </tbody>
                </table>
                <section className="invitation-caption">
                    <div className="invitation-caption-block">
                        <SvgComponent
                            src={`${STATIC_ROOT}/icon/picto-paye.svg`}
                            width={17}
                            height={13}
                        />
                        <p>{i18n.STATE_VALIDATED_FIRST_PURCHASE_DONE}</p>
                    </div>
                    <div className="invitation-caption-block">
                        <SvgComponent
                            src={`${STATIC_ROOT}/icon/picto-valide.svg`}
                            width={17}
                            height={13}
                        />
                        <p>{i18n.STATE_VALIDATED}</p>
                    </div>
                    <div className="invitation-caption-block">
                        <SvgComponent
                            src={`${STATIC_ROOT}/icon/picto-attente.svg`}
                            width={17}
                            height={19}
                        />
                        <p>{i18n.STATE_WAITING}</p>
                    </div>
                </section>
            </main>
        );
    }
}

GodchildrenPage.propTypes = {
    i18n: PropTypes.object.isRequired
};

function mapStateToProps(state) {
    return {
        i18n: state.i18n.page.ACCOUNT.GODCHILDREN,
        user: toPlainObject(state.account.user),
        fetching: state.account.sponsorship.fetching,
        godchildren: state.account.sponsorship.godchildren,
        requests: state.account.sponsorship.requests,
        sendingInvitationReminds: state.sponsorship.sendingInvitationReminds,
        sendingPurchaseReminds: state.sponsorship.sendingPurchaseReminds
    };
}

const mapDispatchToProps = {
    fetchAccountGodchildrenIfNeeded,
    sendInvitationReminds,
    sendPurchaseReminds
};

export default connect(mapStateToProps, mapDispatchToProps)(GodchildrenPage);



// WEBPACK FOOTER //
// ./src/components/pages/account/godchildren/GodchildrenPage.js